/**
 * Soc2Tile — V96-4-9 exec tile on the Dashboard hero row.
 * Verdict, readiness %, gaps, audit-window countdown, one action.
 * Renders when the workspace has an active SOC 2 profile or the readiness
 * endpoint reports criteria (total > 0). Reads only (any member); the action
 * shown to viewers is one they can take (open the page), never a setup CTA.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@aigovops/design-system';
import { getSoc2Readiness, listAuditPeriods, listCertificationProfiles } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { pickSoc2Context, soc2Verdict, windowStatus } from '../../lib/soc2';
import { useOptionalWorkspace } from '../../lib/workspace';
import { CountdownBadge, VerdictPill } from '../../pages/Soc2Readiness';
import './soc2-tile.css';

/** Shared with the Dashboard so the hero bar and framework table show the tile's one SOC 2 number. */
export function useSoc2Tile(slug: string) {
  const readiness = useQuery({ queryKey: queryKeys.soc2Readiness(slug, false), queryFn: () => getSoc2Readiness(slug, false), enabled: !!slug, retry: false });
  const profiles = useQuery({ queryKey: queryKeys.certificationProfiles(slug), queryFn: () => listCertificationProfiles(slug), enabled: !!slug, retry: false });
  const periods = useQuery({ queryKey: queryKeys.auditPeriods(slug), queryFn: () => listAuditPeriods(slug), enabled: !!slug, retry: false });
  const ctx = pickSoc2Context(profiles.data?.profiles ?? [], periods.data?.periods ?? []);
  const hasProfile = !!ctx.profile;
  const data = readiness.data;
  const state: 'hidden' | 'loading' | 'error' | 'ready' =
    data && (hasProfile || data.counts.total > 0) ? 'ready'
    : hasProfile && readiness.isLoading ? 'loading'
    : hasProfile && readiness.error ? 'error'
    : 'hidden';
  const win = ctx.period ? windowStatus(ctx.period) : null;
  const verdict = data ? soc2Verdict(data.counts, win, ctx.period?.status) : null;
  return {
    state, data, ctx, win, verdict,
    periodsLoading: periods.isLoading, periodsError: !!periods.error,
    retry: () => { void readiness.refetch(); void periods.refetch(); },
  };
}

export function Soc2Tile({ slug }: { slug: string }) {
  const { state, data, ctx, win, verdict, periodsLoading, periodsError, retry } = useSoc2Tile(slug);
  const role = useOptionalWorkspace()?.role;
  const canManage = role === 'owner' || role === 'admin';
  if (state === 'hidden') return null;
  if (state === 'loading') return <section className="v96-tile" aria-busy="true" aria-label="SOC 2 status loading"><div className="v96-skel w30" /><div className="v96-skel w60 tall" /><div className="v96-skel w40" /></section>;
  if (state === 'error' || !data || !verdict) return <section className="v96-tile" aria-labelledby="soc2-tile-title">
    <h2 id="soc2-tile-title" className="v96-tile-label">SOC 2</h2>
    <div className="v96-tile-body"><p className="v96-tile-window">SOC 2 status unavailable</p><Button variant="secondary" onClick={retry}>Retry</Button></div>
  </section>;

  const gaps = data.counts.gap;
  const more = data.counts.partial + data.counts.unmapped;
  const type = (ctx.period?.report_type ?? ctx.profile?.report_type) === 'type1' ? 'Type I' : 'Type II';
  // V98-3-2: SOC 2 readiness lives in the SOC 2 program's Prepare tab.
  const base = `/w/${slug}/app/programs/soc2/prepare`;
  const needsPeriod = !periodsLoading && !periodsError && (!ctx.period || win?.phase === 'ended');
  const action = needsPeriod && canManage
    ? { label: win?.phase === 'ended' ? 'Start next audit period' : 'Set audit period', href: `${base}?setup=period` }
    : gaps > 0 ? { label: 'Review gaps', href: `${base}?filter=gap` }
    : more > 0 ? { label: 'Review open items', href: base }
    : { label: 'Open SOC 2 readiness', href: base };

  return <section className="v96-tile" aria-labelledby="soc2-tile-title">
    <div className="v96-tile-top">
      <h2 id="soc2-tile-title" className="v96-tile-label">SOC 2 {type}</h2>
      {periodsError ? null : <VerdictPill verdict={verdict} />}
    </div>
    <div className="v96-tile-body">
      <p className="v96-tile-metric"><span className="v96-tile-value">{Math.round(data.readiness_pct)}%</span><span className="v96-tile-unit">ready</span></p>
      <p className="v96-tile-sub"><strong className={gaps > 0 ? 'bad' : undefined}>{gaps} {gaps === 1 ? 'gap' : 'gaps'}</strong>{more > 0 ? ` · ${more} more need work` : ''}</p>
      {periodsError
        ? <span className="v96-tile-window">Audit window unavailable · <button type="button" className="v96-tile-retry" onClick={retry}>Retry</button></span>
        : win ? <CountdownBadge window={win} verdict={verdict} /> : null}
      <Link className="v75-action primary v96-tile-action" to={action.href}>{action.label}</Link>
    </div>
  </section>;
}
